/**
 * Header business location id + IANA timezone resolution (localStorage + paginated location list).
 */
export const CURRENT_BUSINESS_LOCATION_STORAGE_KEY = "currentBusinessLocationId";

export function readStoredBusinessLocationId() {
  try {
    const raw = window.localStorage.getItem(CURRENT_BUSINESS_LOCATION_STORAGE_KEY);
    const id = raw != null ? Number(raw) : NaN;
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch (e) {
    return null;
  }
}

export function pickBusinessLocationIdFromList(locations) {
  const list = Array.isArray(locations) ? locations : [];
  const defaultLocation = list.find((loc) => loc && loc.isDefaultLocation === true);
  if (defaultLocation && defaultLocation.id != null) {
    return Number(defaultLocation.id);
  }
  const storedId = readStoredBusinessLocationId();
  if (storedId && list.some((loc) => Number(loc && loc.id) === storedId)) {
    return storedId;
  }
  if (list.length > 0 && list[0].id != null) {
    return Number(list[0].id);
  }
  return null;
}

/**
 * Resolves the active business location for API calls.
 * Prefers company default location, then header selection (localStorage), then first in list.
 */
export async function fetchProductionUnitIdForCompany(api, companyId) {
  const cid = Number(companyId);
  if (!Number.isFinite(cid) || cid <= 0) {
    return null;
  }

  try {
    const response = await api.get(`companyProductionUnit/list/${cid}`);
    if (response.status === 1) {
      const locations = (response.data && response.data.list) || [];
      return pickBusinessLocationIdFromList(locations);
    }
  } catch (error) {
    console.error("Error fetching production unit id:", error);
  }

  return null;
}

export function pickTimezoneFromLocations(locations, productionUnitId) {
  const list = Array.isArray(locations) ? locations : [];
  const targetId = Number(productionUnitId);
  const match = list.find((loc) => Number(loc && loc.id) === targetId);
  if (!match) {
    return null;
  }
  return match.timeZone || match.timezoneId || null;
}

/**
 * Resolves header business location id and IANA timezone for display/API calls.
 */
export async function fetchLocationTimezoneForCompany(api, companyId) {
  const cid = Number(companyId);
  if (!Number.isFinite(cid) || cid <= 0) {
    return { productionUnitId: null, timezoneId: null };
  }

  try {
    // TZ: paginate all locations so header TZ resolves for companies with 100+ sites
    const pageSize = 100;
    let pageId = 0;
    let allLocations = [];
    let hasMore = true;

    while (hasMore) {
      const response = await api.post("companyProductionUnit/list", {
        isMultipart: false,
        data: {
          page: { limit: pageSize, pageId },
          sortBy: { property: "name", direction: "ASC" },
          query: "",
          companyId: cid,
        },
      });
      if (response?.status !== 1) {
        break;
      }
      const list = (response.data && response.data.list) || [];
      allLocations = allLocations.concat(list);
      if (list.length < pageSize) {
        hasMore = false;
      } else {
        pageId += 1;
      }
    }

    const productionUnitId = pickBusinessLocationIdFromList(allLocations);
    const timezoneId = pickTimezoneFromLocations(allLocations, productionUnitId);
    return { productionUnitId, timezoneId };
  } catch (error) {
    console.error("Error fetching location timezone:", error);
  }

  return { productionUnitId: null, timezoneId: null };
}
